import React from 'react';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck, ArrowSquareOut, Wallet, Star, Code, GithubLogo } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const repositories = [
  { name: 'defibot-core', description: 'Core trading engine and strategy implementation', language: 'Rust', stars: 2847, forks: 431 },
  { name: 'defibot-contracts', description: 'Smart contracts for multi-chain trading', language: 'Solidity', stars: 1923, forks: 287 },
  { name: 'defibot-sdk', description: 'Official SDK for Python, JavaScript, and Go', language: 'TypeScript', stars: 1456, forks: 198 },
  { name: 'defibot-strategies', description: 'Community-contributed trading strategies', language: 'Python', stars: 892, forks: 156 },
];

const audits = [
  { firm: 'CertiK', date: 'January 2024', status: 'Passed', findings: 'No critical issues' },
  { firm: 'Trail of Bits', date: 'October 2023', status: 'Passed', findings: '2 low severity (fixed)' },
  { firm: 'OpenZeppelin', date: 'June 2023', status: 'Passed', findings: '1 medium, 3 low (fixed)' },
];

const SourceCode = () => {
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="source-code-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-medium">Open Source</span>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-[var(--text-primary)] mt-4 mb-4">Source Code & Audits</h1>
          <p className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">Transparency is at our core. Review our open-source repositories and security audits.</p>
        </motion.div>

        {/* Subscription Gate Overlay */}
        <div className="relative">
          {/* Blurred content preview */}
          <div className="select-none pointer-events-none" style={{ filter: 'blur(6px)', opacity: 0.5 }}>
            {/* Repos preview */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <GithubLogo size={28} className="text-[var(--text-primary)]" weight="bold" />
                <h2 className="text-2xl font-semibold text-[var(--text-primary)]">GitHub Repositories</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {repositories.map((repo) => (
                  <div key={repo.name} className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]">
                    <div className="flex items-center gap-2 mb-3">
                      <Code size={20} className="text-[var(--color-primary)]" weight="bold" />
                      <h3 className="font-semibold text-[var(--text-primary)]">{repo.name}</h3>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mb-4">{repo.description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[var(--color-primary)]" />{repo.language}</span>
                      <span className="text-[var(--text-secondary)]">{repo.stars.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Audits preview */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <ShieldCheck size={28} className="text-[var(--accent-success)]" weight="bold" />
                <h2 className="text-2xl font-semibold text-[var(--text-primary)]">Security Audits</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--border-default)]">
                      <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-secondary)]">Audit Firm</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-secondary)]">Date</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-secondary)]">Status</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-secondary)]">Findings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audits.map((audit, i) => (
                      <tr key={i} className="border-b border-[var(--border-default)]">
                        <td className="py-4 px-4 font-medium text-[var(--text-primary)]">{audit.firm}</td>
                        <td className="py-4 px-4 text-[var(--text-secondary)]">{audit.date}</td>
                        <td className="py-4 px-4"><span className="px-3 py-1 rounded-full bg-[var(--accent-success)]/20 text-[var(--accent-success)] text-sm font-medium">{audit.status}</span></td>
                        <td className="py-4 px-4 text-[var(--text-secondary)]">{audit.findings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Lock overlay */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="absolute inset-0 flex items-center justify-center z-10"
            data-testid="source-code-paywall"
          >
            <div className="max-w-lg w-full mx-4 p-10 rounded-3xl border border-[var(--color-primary)]/30 text-center backdrop-blur-xl"
              style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.08), rgba(0,71,255,0.04), rgba(2,4,10,0.92))' }}>
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(0,71,255,0.15))' }}
              >
                <Lock size={40} className="text-[var(--color-primary)]" weight="bold" />
              </motion.div>
              <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">Subscription Required</h2>
              <p className="text-[var(--text-secondary)] mb-8 leading-relaxed">
                Access to source code repositories, security audit reports, and the bug bounty program is available exclusively for Professional and Enterprise subscribers.
              </p>
              <div className="space-y-4">
                <Link to="/pricing">
                  <motion.button
                    whileHover={{ scale: 1.03, boxShadow: '0 0 40px rgba(0,229,255,0.3)' }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full flex items-center justify-center gap-3 px-8 py-4 rounded-2xl text-lg font-bold transition-all"
                    style={{ background: 'linear-gradient(135deg, #00E5FF, #0047FF)', color: '#02040A' }}
                    data-testid="source-code-upgrade-btn"
                  >
                    <Wallet size={22} weight="bold" /> View Plans & Upgrade
                  </motion.button>
                </Link>
                <div className="flex items-center justify-center gap-3 text-sm text-[var(--text-secondary)]">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} size={14} weight="fill" className={i <= 4 ? 'text-[#00B67A]' : 'text-[#00B67A]/50'} />
                    ))}
                  </div>
                  <span>Trusted by 50,000+ traders</span>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-[var(--border-default)]">
                <p className="text-xs text-[var(--text-secondary)]">
                  Already subscribed?{' '}
                  <Link to="/dashboard" className="text-[var(--color-primary)] hover:underline font-medium">Open Trading Terminal</Link>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SourceCode;
